import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { Button, Copied1Icon, CopyIcon } from '@blade/ui-kit'
import { useBreakpoint, useClipboard } from '@root/shared'
import {
  Alert,
  FadeOverflow,
  mediaMinTablet,
  Separator,
} from '@root/shared/ui-kit'

import type { ClassNameProps } from '@root/shared/react'

type Props = {
  mnemonics: Array<string>
  onNext: () => void
} & ClassNameProps

export const CreateWalletSeedPhrase: React.FC<Props> = ({
  onNext,
  mnemonics,
  className,
}) => {
  const { t } = useTranslation()

  const [copied, setCopied] = useState(false)
  const { copyToClipboard } = useClipboard()
  const breakpoint = useBreakpoint()

  const handleCopyAndNotify = async () => {
    const isCopied = await copyToClipboard(mnemonics.join(' '), {
      showSnackbar: false,
    })

    if (isCopied) {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const renderCopyButton = () => {
    if (copied) {
      return (
        <Button
          size={breakpoint === 'mobile' ? 'h40' : 'h36'}
          fullWidth
          variant="soft"
          color="green"
          radius="rounded"
          iconLeft={<Copied1Icon size={20} />}
        >
          {t('common.copied')}
        </Button>
      )
    }

    return (
      <Button
        size={breakpoint === 'mobile' ? 'h40' : 'h36'}
        fullWidth
        variant="soft"
        radius="rounded"
        iconLeft={<CopyIcon size={20} />}
        onClick={handleCopyAndNotify}
      >
        {t('common.copy')}
      </Button>
    )
  }

  return (
    <Root className={className}>
      <Body variant="vertical" mode="always">
        <SeedPhrase>
          {mnemonics.map((it, idx) => (
            <Word key={it}>
              {idx + 1}. {it}
            </Word>
          ))}
        </SeedPhrase>
      </Body>
      <Footer>
        <Warn
          intent="warning"
          css={css`
            align-self: end;
          `}
        >
          {t('wallet.create.seed-phrase-warning')}
        </Warn>
        <Separator />
        <Actions>
          {renderCopyButton()}
          <Button
            fullWidth
            onClick={onNext}
            variant="solid"
            color="primary"
            size={breakpoint === 'mobile' ? 'h40' : 'h36'}
            radius="rounded"
          >
            {t('common.next')}
          </Button>
        </Actions>
      </Footer>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
`

const Body = styled(FadeOverflow)`
  overflow: auto;
  padding-inline: 12px;
`

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 12px;
  padding-inline: 12px;
`

const Actions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`

const SeedPhrase = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 12px;
  padding-block: 12px;
`
const Warn = styled(Alert)`
  width: 100%;
`

const Word = styled.div`
  background: var(--backgroundA-3);
  color: var(--textA-12);

  display: flex;
  justify-content: center;
  align-items: center;
  height: 40px;
  border-radius: 8px;

  font-size: 14px;
  font-weight: 600;
  line-height: 20px;

  ${mediaMinTablet(css`
    height: 34px;
    color: var(--text-11);

    font-size: 12px;
    font-weight: 500;
    line-height: 16px;
  `)}
`
